import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth, startGoogleLogin } from "@/lib/auth";
import { listNotifications, deleteNotification } from "@/lib/api";
import { Bell, BellRing, Check, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, loading: authLoading } = useAuth() || {};
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    listNotifications().then(setNotes).finally(() => setLoading(false));
  }, [authLoading, user]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-amber" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="dashboard-signin-prompt">
          <Bell className="h-10 w-10 text-amber mx-auto mb-4" />
          <h1 className="font-display text-4xl text-white">Sign in to view alerts</h1>
          <p className="mt-3 text-zinc-400 font-body">
            We save your Turkish-dub alerts to your account so you can pick up where you left off.
          </p>
          <Button
            onClick={startGoogleLogin}
            className="mt-8 bg-amber hover:bg-amber-hover text-obsidian font-heading"
            data-testid="dashboard-login-btn"
          >
            Sign in with Google
          </Button>
        </div>
      </div>
    );
  }

  const remove = async (id) => {
    await deleteNotification(id);
    setNotes((n) => n.filter((x) => x.id !== id));
    toast.success("Alert removed");
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-5xl mx-auto px-6 py-14" data-testid="dashboard-main">
        <div className="text-[11px] uppercase tracking-[0.25em] text-amber font-heading mb-2">My Watchlist</div>
        <h1 className="font-display text-4xl sm:text-5xl text-white">Alerts armed</h1>
        <p className="mt-3 text-zinc-400 font-body max-w-2xl">
          You&apos;ll be notified the moment Turkish dubbing appears on any platform for these titles.
        </p>

        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-amber" /></div>
        ) : notes.length === 0 ? (
          <div className="mt-10 border border-white/10 rounded-md p-10 text-center bg-obsidian-surface/60" data-testid="dashboard-empty">
            <BellRing className="h-8 w-8 text-amber mx-auto mb-3" />
            <p className="font-display text-2xl text-white">No alerts yet</p>
            <p className="text-zinc-500 font-body mt-2">Find a title and arm an alert from its page.</p>
            <Button
              onClick={() => navigate("/")}
              className="mt-6 bg-amber hover:bg-amber-hover text-obsidian font-heading"
              data-testid="dashboard-browse-btn"
            >
              Browse titles
            </Button>
          </div>
        ) : (
          <ul className="mt-8 space-y-3 stagger" data-testid="dashboard-notifications-list">
            {notes.map((n) => (
              <li
                key={n.id}
                className="flex items-center gap-4 border border-white/10 rounded-md p-4 bg-obsidian-surface/60 hover:border-amber/40 transition"
                data-testid={`notification-${n.id}`}
              >
                <Link to={`/title/${n.title_id}`} className="shrink-0">
                  <img src={n.poster} alt={n.title} className="h-20 w-14 rounded object-cover border border-white/10" />
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/title/${n.title_id}`} className="font-display text-xl text-white hover:text-amber transition">
                    {n.title} <span className="text-zinc-500 text-base">· {n.year}</span>
                  </Link>
                  <div className="mt-1 flex items-center gap-2 text-xs font-heading">
                    {n.has_turkish_dub ? (
                      <span className="inline-flex items-center gap-1 text-emerald-300">
                        <Check className="h-3 w-3" /> Turkish dub available
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber">
                        <BellRing className="h-3 w-3" /> Waiting for Turkish dub
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => remove(n.id)}
                  className="p-2 rounded hover:bg-white/5 text-zinc-500 hover:text-red-400 transition"
                  data-testid={`delete-notification-${n.id}`}
                  aria-label="Remove alert"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
